<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { UseAtcWatchlist } from '../../composables/useAtcWatchlist'
import type { UseMetaWatchlist } from '../../composables/useMetaWatchlist'

import AtcWatchlistTab from './AtcWatchlistTab.vue'
import MetaWatchlistTab from './MetaWatchlistTab.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })
const props = defineProps<{ meta: UseMetaWatchlist; atc: UseAtcWatchlist }>()
const emit = defineEmits<{ saved: [] }>()
const { t } = useI18n()
const activeTab = ref<'meta' | 'google'>('meta')
const currentLoading = computed(() =>
  activeTab.value === 'meta'
    ? props.meta.state.value === 'loading'
    : props.atc.state.value === 'loading',
)
const currentError = computed(() =>
  activeTab.value === 'meta'
    ? props.meta.state.value === 'error'
    : props.atc.state.value === 'error',
)
const currentSaving = computed(() =>
  activeTab.value === 'meta' ? props.meta.saving.value : props.atc.saving.value,
)
const currentCanSave = computed(() =>
  activeTab.value === 'meta' ? props.meta.canSave.value : props.atc.canSave.value,
)
const currentSubmitted = computed(() =>
  activeTab.value === 'meta' ? props.meta.hasSubmitted.value : props.atc.hasSubmitted.value,
)
const currentErrorCount = computed(() =>
  activeTab.value === 'meta'
    ? props.meta.errorCount.value
    : Object.keys(props.atc.validation.value.errors).length,
)
const tabLogo = (tab: 'meta' | 'google'): string => resolveStaticPlatformLogo(tab) ?? ''

function reset(): void {
  props.meta.resetDrafts()
  props.atc.resetDrafts()
  activeTab.value = 'meta'
}

function close(): void {
  open.value = false
  reset()
}

async function save(): Promise<void> {
  const outcome = activeTab.value === 'meta' ? await props.meta.save() : await props.atc.save()
  if (outcome.status === 'saved' || outcome.status === 'deleted') {
    open.value = false
    emit('saved')
  }
}

watch(open, (value) => {
  if (value) reset()
})
</script>

<template>
  <Modal v-model="open" size="xl" @close="close">
    <template #title>
      <span class="watchlist-modal__title block">{{
        t('workspaceDefinitions.watchlist.host.title')
      }}</span>
      <span class="watchlist-modal__subtitle block mt-2xs text-muted text-[0.875rem] font-normal">{{
        t('workspaceDefinitions.watchlist.host.description')
      }}</span>
    </template>

    <div
      class="watchlist-modal__tabs flex gap-sm mb-lg pb-md border-b border-b-border"
      role="tablist"
    >
      <button
        v-for="tab in ['meta', 'google'] as const"
        :key="tab"
        type="button"
        role="tab"
        class="watchlist-modal__tab inline-flex items-center gap-sm py-sm px-md border border-border rounded-md text-muted text-[0.875rem] font-semibold"
        :class="{ 'watchlist-modal__tab--active': activeTab === tab }"
        :aria-selected="activeTab === tab"
        @click="activeTab = tab"
      >
        <PlatformLogo :platform="tab" :src="tabLogo(tab)" :size="20" />
        {{ t(`workspaceDefinitions.watchlist.host.tabs.${tab}`) }}
      </button>
    </div>

    <Alert v-if="currentError" tone="danger">
      {{
        t(
          activeTab === 'meta'
            ? (meta.loadErrorKey.value ?? 'workspaceDefinitions.watchlist.meta.error.load')
            : (atc.loadErrorKey.value ?? 'workspaceDefinitions.watchlist.atc.error.load'),
        )
      }}
      <Button
        variant="outline"
        size="sm"
        @click="activeTab === 'meta' ? meta.load() : atc.load()"
        >{{ t('workspaceSettings.error.retry') }}</Button
      >
    </Alert>
    <MetaWatchlistTab v-else-if="activeTab === 'meta' && !currentLoading" :watchlist="meta" />
    <AtcWatchlistTab v-else-if="activeTab === 'google' && !currentLoading" :watchlist="atc" />

    <template #footer>
      <p
        v-if="currentSubmitted && currentErrorCount"
        class="watchlist-modal__errors m-0 text-warning text-[0.75rem] font-semibold"
      >
        {{ t('workspaceDefinitions.watchlist.host.validation', currentErrorCount) }}
      </p>
      <span class="watchlist-modal__spacer flex-1" />
      <Button variant="outline" @click="close">{{ t('common.cancel') }}</Button>
      <Button
        :loading="currentSaving"
        :disabled="currentLoading || currentError || !currentCanSave"
        data-testid="watchlist-save"
        @click="save"
      >
        {{
          t(
            activeTab === 'google'
              ? 'workspaceDefinitions.watchlist.atc.action.save'
              : 'common.save',
          )
        }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.watchlist-modal__tab--active {
  border-color: var(--ui-color-primary-subtle);
  background-color: var(--ui-color-primary-subtle);
  color: var(--ui-color-primary);
}
</style>
